import _typeof from "@babel/runtime/helpers/typeof";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
export function createAutocompletePropGetters(_ref) {
  var useEffect = _ref.useEffect,
    useId = _ref.useId,
    useMemo = _ref.useMemo,
    useRef = _ref.useRef,
    useState = _ref.useState;
  return function usePropGetters(_ref2) {
    var indices = _ref2.indices,
      indicesConfig = _ref2.indicesConfig,
      onRefine = _ref2.onRefine,
      globalOnSelect = _ref2.onSelect;
    var getElementId = createGetElementId(useId());
    var rootRef = useRef(null);
    var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isOpen = _useState2[0],
      setIsOpen = _useState2[1];
    var _useState3 = useState(undefined),
      _useState4 = _slicedToArray(_useState3, 2),
      activeDescendant = _useState4[0],
      setActiveDescendant = _useState4[1];
    var _useMemo = useMemo(function () {
        return buildItems({
          indices: indices,
          indicesConfig: indicesConfig,
          getElementId: getElementId
        });
      }, [indices, indicesConfig, getElementId]),
      items = _useMemo.items,
      itemsIds = _useMemo.itemsIds;
    useEffect(function () {
      var onBodyClick = function onBodyClick(event) {
        var _unwrapRef;
        if ((_unwrapRef = unwrapRef(rootRef)) !== null && _unwrapRef !== void 0 && _unwrapRef.contains(event.target)) {
          return;
        }
        setIsOpen(false);
      };
      document.body.addEventListener('click', onBodyClick);
      return function () {
        document.body.removeEventListener('click', onBodyClick);
      };
    }, [rootRef]);
    var getNextActiveDescendent = function getNextActiveDescendent(key) {
      switch (key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          {
            var prevIndex = itemsIds.indexOf(activeDescendant || '') - 1;
            return itemsIds[prevIndex] || itemsIds[itemsIds.length - 1];
          }
        case 'ArrowRight':
        case 'ArrowDown':
          {
            var nextIndex = itemsIds.indexOf(activeDescendant || '') + 1;
            return itemsIds[nextIndex] || itemsIds[0];
          }
        default:
          return undefined;
      }
    };
    var submit = function submit() {
      var actualActiveDescendant = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : activeDescendant;
      setIsOpen(false);
      if (actualActiveDescendant && items.has(actualActiveDescendant)) {
        var _getQuery;
        var _ref3 = items.get(actualActiveDescendant),
          _item = _ref3.item,
          _ref3$config = _ref3.config,
          indexOnSelect = _ref3$config.onSelect,
          getQuery = _ref3$config.getQuery,
          getURL = _ref3$config.getURL;
        var actualOnSelect = indexOnSelect !== null && indexOnSelect !== void 0 ? indexOnSelect : globalOnSelect;
        actualOnSelect({
          item: _item,
          query: (_getQuery = getQuery === null || getQuery === void 0 ? void 0 : getQuery(_item)) !== null && _getQuery !== void 0 ? _getQuery : '',
          url: getURL === null || getURL === void 0 ? void 0 : getURL(_item),
          setQuery: function setQuery(query) {
            return onRefine(query);
          }
        });
        setActiveDescendant(undefined);
      }
    };
    return {
      getInputProps: function getInputProps() {
        return {
          id: getElementId('input'),
          role: 'combobox',
          'aria-autocomplete': 'list',
          'aria-expanded': isOpen,
          'aria-haspopup': 'grid',
          'aria-controls': getElementId('panel'),
          'aria-activedescendant': activeDescendant,
          onFocus: function onFocus() {
            return setIsOpen(true);
          },
          onKeyDown: function onKeyDown(event) {
            if (event.key === 'Escape') {
              setActiveDescendant(undefined);
              setIsOpen(false);
              return;
            }
            switch (event.key) {
              case 'ArrowLeft':
              case 'ArrowUp':
              case 'ArrowRight':
              case 'ArrowDown':
                {
                  var _document$getElementB;
                  var nextActiveDescendent = getNextActiveDescendent(event.key);
                  setActiveDescendant(nextActiveDescendent);
                  (_document$getElementB = document.getElementById(nextActiveDescendent)) === null || _document$getElementB === void 0 ? void 0 : _document$getElementB.scrollIntoView(false);
                  event.preventDefault();
                  break;
                }
              case 'Enter':
                {
                  submit();
                  break;
                }
              case 'Tab':
                setIsOpen(false);
                break;
              default:
                return;
            }
          },
          onKeyUp: function onKeyUp(event) {
            switch (event.key) {
              case 'ArrowLeft':
              case 'ArrowUp':
              case 'ArrowRight':
              case 'ArrowDown':
              case 'Escape':
              case 'Return':
                event.preventDefault();
                return;
              default:
                setActiveDescendant(undefined);
                break;
            }
          }
        };
      },
      getItemProps: function getItemProps(item, index) {
        var id = getElementId('item', item.__indexName, index);
        return {
          id: id,
          role: 'row',
          'aria-selected': id === activeDescendant,
          onSelect: function onSelect() {
            return submit(id);
          }
        };
      },
      getPanelProps: function getPanelProps() {
        return {
          hidden: !isOpen,
          id: getElementId('panel'),
          role: 'grid',
          'aria-labelledby': getElementId('input')
        };
      },
      getRootProps: function getRootProps() {
        return {
          ref: rootRef
        };
      }
    };
  };
}
function buildItems(_ref4) {
  var indices = _ref4.indices,
    indicesConfig = _ref4.indicesConfig,
    getElementId = _ref4.getElementId;
  var itemsIds = [];
  var items = new Map();
  for (var i = 0; i < indicesConfig.length; i++) {
    var _indices$i;
    var config = indicesConfig[i];
    var hits = ((_indices$i = indices[i]) === null || _indices$i === void 0 ? void 0 : _indices$i.hits) || [];
    for (var position = 0; position < hits.length; position++) {
      var itemId = getElementId('item', config.indexName, position);
      items.set(itemId, {
        item: hits[position],
        config: config
      });
      itemsIds.push(itemId);
    }
  }
  return {
    items: items,
    itemsIds: itemsIds
  };
}
function createGetElementId(autocompleteId) {
  return function getElementId() {
    var prefix = 'autocomplete';
    for (var _len = arguments.length, suffixes = new Array(_len), _key = 0; _key < _len; _key++) {
      suffixes[_key] = arguments[_key];
    }
    return "".concat(prefix).concat(autocompleteId).concat(suffixes.join(':'));
  };
}

/**
 * Returns the framework-agnostic value of a ref.
 */
function unwrapRef(ref) {
  return ref.current && _typeof(ref.current) === 'object' && 'base' in ref.current ? ref.current.base // Preact
  : ref.current; // React
}